import usePurchase from '@/hooks/usePurchase';
import { usePlans } from '@/hooks/usePlans';
import { useNavigate } from 'react-router-dom';

import {
  CheckCircle2,
  Users,
  Monitor,
  Flag,
  Globe,
  Building2,
  Gauge,
  Sparkles,
} from 'lucide-react';

import styled from 'styled-components';
import { breakpoints } from '@/layouts/breakpoints';
import { FREE_TRIAL_ENABLED } from '@/lib/features';

const StyledComp = styled.div`
  @keyframes pricePulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  padding: 80px 40px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  max-width: 1600px;
  margin: 0 auto;
  gap: 0;
  border-bottom: 1px solid rgba(var(--greyr), 0.3);
  background-color: rgba(var(--whiter), 0.3);
  position: relative;
  z-index: 2;

  @media (max-width: ${breakpoints.large}) {
    padding: 40px 20px;
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${breakpoints.small}) {
    padding: 32px 16px;
    grid-template-columns: 1fr;
  }

  .tier {
    padding: 32px;
    border-right: 1px solid rgba(var(--greyr), 0.3);
    display: flex;
    flex-direction: column;
    position: relative;

    &:last-child {
      border-right: none;
    }

    @media (max-width: ${breakpoints.large}) {
      &:nth-child(2) {
        border-right: none;
      }
      &:nth-child(3) {
        border-top: 1px solid rgba(var(--greyr), 0.3);
      }
      &:nth-child(4) {
        border-right: none;
        border-top: 1px solid rgba(var(--greyr), 0.3);
      }
    }

    @media (max-width: ${breakpoints.small}) {
      border-right: none;
      border-top: 1px solid rgba(var(--greyr), 0.3);

      &:first-child {
        border-top: none;
      }
    }

    /* Trial callout */
    .trial-badge {
      position: absolute;
      top: 20px;
      right: 20px;
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 5px 10px 5px 8px;
      border-radius: 20px;
      color: var(--green);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.2px;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(var(--greyr), 0.4);

      svg {
        flex-shrink: 0;
      }

      @media (max-width: ${breakpoints.small}) {
        position: static;
        align-self: flex-start;
        margin-bottom: 16px;
      }
    }

    /* Name */
    .tier-name {
      font-size: 12px;
      color: var(--grey);
      font-weight: 700;
      margin-bottom: 20px;
    }

    /* Price */
    .tier-price {
      margin-bottom: 20px;

      .price-row {
        display: flex;
        align-items: baseline;
        gap: 2px;

        .currency {
          font-size: 20px;
          line-height: 1;
        }

        .amount {
          font-size: 40px;
          line-height: 1;

          @media (max-width: ${breakpoints.xsmall}) {
            font-size: 32px;
          }
        }

        .period {
          font-size: 14px;
          color: var(--grey);
          margin-left: 4px;
        }

        .custom-label {
          font-size: 26px;
          line-height: 1.2;
        }
      }

      .price-note {
        font-size: 12px;
        color: var(--grey);
        margin-top: 4px;
      }
    }

    /* CTA */
    .tier-action {
      margin-bottom: 24px;

      button {
        width: 100%;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        border: 1px solid;

        &.primary {
          background-color: var(--black);
          color: var(--white);
          border-color: var(--black);

          &:hover {
            opacity: 0.75;
          }
        }

        &.secondary {
          background-color: transparent;
          color: var(--black);
          border: 1px solid rgba(var(--greyr), 0.3);

          &:hover {
            background-color: rgba(var(--greyr), 0.3);
          }
        }
      }
    }

    /* Divider */
    .tier-divider {
      border: none;
      border-top: 1px solid rgba(var(--greyr), 0.3);
      margin: 0 0 20px 0;
    }

    /* Includes */
    .includes-label {
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 12px;
    }

    .includes-items {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 4px;

      .includes-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: var(--black);

        svg {
          flex-shrink: 0;
          color: var(--grey);
        }

        span {
          padding-bottom: 1px;
        }
      }
    }

    /* Accordions */
    .accordion-section {
      border-top: 1px solid rgba(var(--greyr), 0.3);
      margin-top: 10px;
      padding-top: 5px;
      margin-bottom: -15px;
      padding: 6px 0;

      .accordion-trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 6px 0;
        background: none;
        border: none;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        color: var(--black);
        text-align: left;

        .accordion-icon {
          flex-shrink: 0;
          color: var(--purple);
        }

        .chevron {
          margin-left: auto;
          flex-shrink: 0;
          color: var(--grey);
          transition: transform 0.2s;

          &.open {
            transform: rotate(180deg);
          }
        }
      }
    }

    /* Features */
    .tier-features {
      margin-top: 20px;
      border-top: 1px solid rgba(var(--greyr), 0.3);
      padding-top: 20px;

      .features-prefix {
        font-size: 13px;
        color: var(--grey);
        margin-bottom: 14px;

        strong {
          color: var(--black);
          font-weight: 600;
        }
      }

      .feature-list {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .feature {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          cursor: pointer;

          svg {
            flex-shrink: 0;
            color: var(--green);
          }

          span {
            padding-bottom: 1px;
            border-bottom: 1px dashed rgba(var(--greyr), 0.6);
            line-height: 1.4;
          }
        }
      }
    }
  }
`;

const pricingTiers = [
  {
    name: 'FREE',
    id: 'free',
    price: 0,
    annualPrice: 0,
    priceNote: 'Free forever',
    buttonText: 'Start for free',
    buttonStyle: 'secondary',
    seats: '1,000 API calls/month',
    demos: '1 year of history',
    trialBadge: null,
    accordions: [
       { id: 'copilot', icon: Flag, label: 'US markets only' },
    ],
    featuresPrefix: null,
    features: [
      { name: 'Stocks', url: '/products/stocks' },
      { name: 'Company profiles', url: '/products/corporate-profiles' },
    ],
  },
  {
    name: 'STARTER',
    id: 'starter',
    price: 49.99,
    annualPrice: 39.99,
    priceNote: 'Billed monthly',
    buttonText: 'Get Starter',
    buttonStyle: 'secondary',
    seats: '50,000 API calls/month',
    demos: '25 years of history',
    trialBadge: null,
    accordions: [
       { id: 'copilot', icon: Globe, label: 'Global coverage' },
    ],
    featuresPrefix: 'Free',
    features: [
      { name: 'ETFs', url: '/products/etfs' },
      { name: 'Forex', url: '/products/forex' },
      { name: 'Indices', url: '/products/indices' },
      { name: 'Crypto', url: '/products/crypto' },
      { name: 'Earnings', url: '/products/earnings' },
      { name: 'Disclosures & filings', url: '/products/disclosures' },
      { name: 'Economic data', url: '/products/economics' },
      { name: 'News', url: '/products/news' },
    ],
  },
  {
    name: 'PRO',
    id: 'pro',
    price: 199.99,
    annualPrice: 159.99,
    priceNote: 'Billed monthly',
    buttonText: 'Start with Pro',
    buttonStyle: 'primary',
    seats: '1,000,000 API calls/month',
    demos: '50+ years of history',
    trialBadge: FREE_TRIAL_ENABLED ? '14-day free trial' : null,
    accordions: [
       { id: 'copilot', icon: Globe, label: 'Global coverage' },
       { id: 'ent', icon: Building2, label: 'Commercial use' },
    ],
    featuresPrefix: 'Starter',
    features: [
      { name: 'Futures', url: '/products/futures' },
      { name: 'Credit ratings', url: '/products/credit' },
      { name: 'ESG rankings', url: '/products/esg' },
      { name: 'Supply chain', url: '/products/supply-chain' },
      { name: 'Sentiment analysis', url: '/products/sentiment' },
      { name: 'Web traffic', url: '/products/webtraffic' },
    ],
  },
  {
    name: 'ENTERPRISE',
    id: 'enterprise',
    price: null,
    annualPrice: null,
    priceNote: 'Contact us for details',
    buttonText: 'Contact for pricing',
    buttonStyle: 'secondary',
    seats: 'Unlimited API calls',
    demos: 'Unlimited history',
    trialBadge: null,
    accordions: [
       { id: 'copilot', icon: Globe, label: 'Global coverage' },
       { id: 'ent', icon: Building2, label: 'Commercial use' },
       { id: 'slas', icon: Gauge, label: 'Custom SLAs' },
    ],
    featuresPrefix: 'Pro',
    features: [
      { name: 'Custom seats', url: '/contact' },
      { name: 'Offline demos', url: '/contact' },
      { name: 'Audit logs', url: '/contact' },
      { name: 'Priority support', url: '/contact' },
      { name: 'Demo consultation', url: '/contact' },
    ],
  },
];


 const Comp = ({ billingPeriod }) => {
  const purchase = usePurchase();
  const navigate = useNavigate();
  const { plans } = usePlans();

  const parsePrice = (s: any) => {
    if (s == null) return null;
    const n = parseFloat(String(s).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  };

  const displayPrices = plans?.displayPrices;
  const tiers = pricingTiers.map((t) => {
    const dp = displayPrices?.[t.id];
    if (!dp) return t;
    return {
      ...t,
      price: parsePrice(dp.monthly) ?? t.price,
      annualPrice: parsePrice(dp.annual) ?? t.annualPrice,
    };
  });

  const buy = (plan: string) => {
    purchase.capturePurchaseIntent(plan, billingPeriod);
  };

  const CTAS = {
    free: () => { navigate('/login') },
    starter: () => { buy('starter') },
    pro: () => { buy('pro') },
    enterprise: () => { navigate('/contact') },
  }

  return (
    <StyledComp>
      {tiers.map((tier, index) => (
        <div key={index} className="tier">

          {(tier.trialBadge && billingPeriod === 'monthly') && (
            <div className="trial-badge">
              <Sparkles size={12} />
              {tier.trialBadge}
            </div>
          )}

          <div className="tier-name sans">{tier.name}</div>

          <div className="tier-price">
            {!plans && tier.price !== null ? (
              <div className="price-row" style={{ height: 40 }}>
                <span style={{ display: 'block', width: 120, height: 32, borderRadius: 6, background: 'rgba(var(--greyr), 0.15)', animation: 'pricePulse 1.2s ease-in-out infinite' }} />
              </div>
            ) : tier.price !== null ? (
              <>
                <div className="price-row">
                  <span className="currency">$</span>
                  <span className="amount" data-redact>
                    {billingPeriod === 'annual'
                      ? (tier.annualPrice ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : (tier.price ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="period">/mo</span>
                </div>
                <div className="price-note">
                  {billingPeriod === 'annual' && tier.annualPrice !== null && tier.annualPrice > 0
                    ? 'Billed annually'
                    : tier.priceNote}
                </div>
              </>
            ) : (
              <>
                <div className="price-row">
                  <span className="custom-label">Custom pricing</span>
                </div>
                <div className="price-note">{tier.priceNote}</div>
              </>
            )}
          </div>

          <div className="tier-action">
            <button
              className={tier.buttonStyle}
              onClick={() => {CTAS[tier.id]()}}
            >
              {tier.buttonText}
            </button>
          </div>

          <hr className="tier-divider" />

          <div className="includes-label">Includes:</div>
          <div className="includes-items">
            <div className="includes-item">
              <Users size={14} />
              <span>{tier.seats}</span>
            </div>
            <div className="includes-item">
              <Monitor size={14} />
              <span>{tier.demos}</span>
            </div>
          </div>

          <div  className="accordion-section">
            {tier.accordions.map(({ id, icon: Icon, label}) => {
              const key = `${tier.id}-${id}`;
              return (
                <button
                  key={key}
                  className="accordion-trigger"
                >
                  <Icon size={16} className="accordion-icon" />
                  <span>{label}</span>
                </button>
              );
            })}

          </div>
          <div className="tier-features">
            {tier.featuresPrefix && (
              <div className="features-prefix">
                All features in <strong>{tier.featuresPrefix}</strong>, and:
              </div>
            )}
            <div className="feature-list">
              {tier.features.map((feature, idx) => (
                <div key={idx} className="feature" onClick={() => navigate(feature.url)}>
                  <CheckCircle2 size={16} />
                  <span>{feature.name}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      ))}
    </StyledComp>
  );
};

export default Comp;
